/** Where a single file upload of a part is: travelling, being attached to the part, or nothing. */
export type UploadStage = 'idle' | 'uploading' | 'saving';

/**
 * The stage an upload change event moves to (antd statuses), or null when the status does not
 * change it. "done" means the file arrived: it still has to be attached to the part.
 */
export const uploadStageForStatus = (status?: string): UploadStage | null => {
    switch (status) {
        case 'uploading':
            return 'uploading';
        case 'done':
            return 'saving';
        case 'error':
        case 'removed':
            return 'idle';
        default:
            return null;
    }
};

export const isUploadInProgress = (stage: UploadStage): boolean => stage !== 'idle';

/**
 * Class of the label of the upload button. The button is disabled while the upload runs, and the
 * disabled color of the design system (25% black) is unreadable on the card in light and dark
 * mode: a busy label carries its own readable color (see `.upload-busy-label`).
 */
export const getUploadLabelClass = (stage: UploadStage): string | undefined =>
    stage === 'idle' ? undefined : 'upload-busy-label';

/** The text of the upload button, the same for every file type (2D and 3D). */
export const getUploadButtonLabel = (
    stage: UploadStage,
    filename: string,
    percent: number,
    idleLabel: string
): string => {
    // The percentage only counts the bytes sent. At 100% the server still stores the file and builds
    // its preview, which can take seconds: say so instead of showing a frozen "100%".
    if (stage === 'uploading' && percent >= 100) return `Processing ${filename}…`;
    if (stage === 'uploading') return `Uploading ${filename}${percent > 0 ? ` (${percent}%)` : '…'}`;
    if (stage === 'saving') return `Saving ${filename}…`;
    return idleLabel;
};
